import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeDocumentClientModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentPaymentListItemModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, PurchaseBankAccountModel, PurchaseEInvoiceDocumentItemModel, PurchaseEInvoiceDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, getStartOfDate, pickBy } from '@skylab-shared/utils';
import { PurchaseEInvoiceDocumentBO } from './purchase-e-invoice-document.bo';

@Repository({
    boType: PurchaseEInvoiceDocumentBO,
    apiController: TaggedUrl.join('purchase', [Tags.Api]),
    dto: {
        get: PurchaseGetDocumentDTO,
        update: PurchaseUpdateDocumentDTO
    }
})
@Injectable({ providedIn: 'root'})
export class PurchaseEInvoiceDocumentRepository extends RepositoryBase<PurchaseEInvoiceDocumentBO, PurchaseEInvoiceDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO> {
    public constructor(
        http: Http,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(http as HttpService);
    }

    protected createBO(): PurchaseEInvoiceDocumentBO {
        return new PurchaseEInvoiceDocumentBO(this.weaver as WeaverBase, this.queryBus as any as QueryBusBase);
    }

    protected async fromPlainObjectAsync(item: PurchaseGetDocumentDTO): Promise<PurchaseEInvoiceDocumentModel> {
        return new PurchaseEInvoiceDocumentModel(pickBy<Partial<PurchaseEInvoiceDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            favourite: item.favourite,

            ddkState: item.ddkState,
            eppState: item.eppState,

            ksefDocumentId: item.ksefDocumentId!,
            ksefDocumentKind: item.ksefDocumentKind,
            ksefHeaderId: item.ksefHeaderId,
            ksefHash: item.ksefHash,
            ksefCollectiveIdentifier: item.ksefCollectiveIdentifier,
            ksefSubmissionMode: item.ksefSubmissionMode,
            dateOfAssigningKsefNumber: item.dateOfAssigningKsefNumber,

            issuedByVatPayer: item.issuedByVatPayer,

            cashBasis: item.cashBasis,
            seller: deserializeDocumentClientModel(item.seller),
            bankAccount: item.sellerBankAccount
                ? new PurchaseBankAccountModel(pickBy<PurchaseBankAccountModel>({
                    bankName: item.sellerBankAccount.bankName,
                    number: item.sellerBankAccount.number,
                    status: item.sellerBankAccount.whiteListStatus,
                    statusDate: item.sellerBankAccount.whiteListLastDateCheck,
                    statusId: item.sellerBankAccount.whiteListCheckingId
                }, val => val !== void 0))
                : null,
            documentNumber: item.documentNumber,
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                manual: item.exchangeRate.manual,
                units: item.exchangeRate.units,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
            }, val => val !== void 0)) : null,
            externalDocumentNumber: item.externalDocumentNumber,
            kind: item.kind,
            note: item.note,
            numberingId: item.numberingId,
            splitPayment: item.splitPayment,
            jpkVatGroups: item.jpkVatDocumentGroups,

            grossValue: item.totalBaseCurrencyGrossValue,
            currencyGrossValue: item.exchangeRate ? item.totalGrossValue : null,

            netValue: item.totalNetValue,

            fixedAsset: item.fixedAsset,
            transactionLocation: item.transactionLocation,

            deliveryDate: item.deliveryDate,
            issueDate: item.issueDate,
            receiptDate: item.receiptDate,
            vatDeductionDate: item.vatDeductionDate,

            dueDate: item.dueDate ? new Date(item.dueDate) : null,
            dueDateDays: item.dueDate && item.issueDate ? dateDiffInDays(item.dueDate, item.issueDate) : null,
            customDueDateDescription: item.customDueDateDescription,

            payments: new ObservableCollection(item.payments.map(item => {
                return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                    id: item.id,
                    date: item.date,
                    value: item.amount,
                    onDocument: item.onDocument
                }, val => val !== void 0));
            })),

            recipient: item.recipient,
            issuer: item.issuer,

            calculationMethod: item.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net,

            communityTransactionId: item.communityTransactionId,

            currencyId: item.currencyId,
            paymentMethodId: item.paymentMethodId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new PurchaseEInvoiceDocumentItemModel(pickBy<Partial<PurchaseEInvoiceDocumentItemModel>>({
                    adjusted: documentItem.adjusted,
                    amountsSource: documentItem.amountsSource,
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    id: documentItem.id,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    productId: documentItem.productId,
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    zal15: documentItem.zal15,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    quantity: documentItem.quantity,
                    unitSymbol: documentItem.unitSymbol,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId
                }, val => val !== void 0));
            })),
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.currencyGross ?? item.gross,
                    netValue: item.currencyNet ?? item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.currencyVat ?? item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })),
            vatTableItemsInBaseCurrency: item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })) : new ObservableCollection()
        }, val => val !== void 0));
    }

    protected async preparePlainObjectAsync(model: PurchaseEInvoiceDocumentModel): Promise<PurchaseUpdateDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            favourite: model.favourite,

            recipient: model.recipient,
            issuer: model.issuer,

            calculationMethod: model.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net,
            cashBasis: model.cashBasis,
            seller: serializeDocumentClientModel(model.seller),
            sellerBankAccount: model.bankAccount
                ? {
                    bankName: model.bankAccount.bankName,
                    number: model.bankAccount.number,
                    whiteListStatus: model.bankAccount.status,
                    whiteListLastDateCheck: model.bankAccount.statusDate,
                    whiteListCheckingId: model.bankAccount.statusId
                }
                : null,
            documentNumber: model.documentNumber,
            externalDocumentNumber: model.externalDocumentNumber,
            kind: model.kind,
            note: model.note,
            numberingId: model.numberingId,
            splitPayment: model.splitPayment,
            jpkVatDocumentGroups: model.jpkVatGroups,

            fixedAsset: model.fixedAsset,

            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            receiptDate: model.receiptDate ? getStartOfDate(model.receiptDate) : null,
            deliveryDate: getStartOfDate(model.deliveryDate),
            vatDeductionDate: getStartOfDate(model.vatDeductionDate),
            dueDate: model.dueDate ? getStartOfDate(model.dueDate) : null,
            customDueDateDescription: model.customDueDateDescription,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),

            transactionLocation: model.transactionLocation,

            communityTransactionId: model.communityTransactionId,
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            currencyId: model.currencyId,
            paymentMethodId: model.paymentMethodId,
        };
    }
}
